<div class="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
  <!-- assigned: full-width banner on the primary colour with white letters — unmistakable that the shift
       was assigned to the employee -->
  <div *ngIf="assigned"
       class="relative z-10 flex items-center gap-1.5 bg-theme-primary-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="m16 11 2 2 4-4"/>
    </svg>
    Sie sind eingeteilt
  </div>

  <!-- shard colour accent on the left (always) -->
  <div [style.background-color]="event.color" class="absolute inset-y-0 left-0 w-1.5"></div>

  <div class="pl-4 pr-3 py-3 flex flex-col gap-2">
    <!-- header: title + time (left) · status badge (top right, only when not assigned) -->
    <div class="flex items-start gap-3">
      <div class="flex-1 min-w-0">
        <div class="flex flex-wrap items-center gap-x-2">
          <p class="text-sm font-semibold text-gray-900 truncate">{{ title }}</p>
          <!-- #664: a virtual event is planning-only, but that is an internal distinction. To an employee it is
               an ordinary event — deliberately no badge here; the answer buttons work exactly the same. -->
        </div>
        <!-- grouped by day → only the time, not the date -->
        <p class="text-xs text-gray-500">
          {{ event.start | date: "HH:mm" }} – {{ event.end | date: "HH:mm" }}
          @if (event.shardName) {
            <span class="text-gray-500">· {{ event.shardName }}</span>
          }
        </p>
      </div>

      <span *ngIf="!assigned" class="flex-none rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap"
            [ngClass]="statusClass">
        {{ statusLabel }}
      </span>
    </div>

    <!-- description (only when there's room: hidden in the minimal assigned variant) -->
    <p *ngIf="event.publicDescription != '' && !assigned"
       class="text-sm font-light text-gray-600 whitespace-pre-line">{{ event.publicDescription }}</p>

    <ng-container *ngIf="assigned; else respond">
      <!-- ===== assigned variant: the shift time(s) + calendar export, on the tinted card ===== -->
      <div class="flex items-center gap-2 flex-wrap">
        <span *ngFor="let shift of responses"
              class="inline-flex items-center gap-1.5 rounded-lg bg-theme-primary-50 px-2.5 py-1 text-xs font-medium text-theme-primary-700 ring-1 ring-theme-primary-100">
          <svg class="h-3.5 w-3.5 text-theme-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
          </svg>
          {{ shift.start | date: "HH:mm" }} – {{ shift.end | date: "HH:mm" }}
          <span *ngIf="shift.locationName" class="text-theme-primary-400">· {{ shift.locationName }}</span>
        </span>
        <button type="button" (click)="IcsService.download(responses)"
                class="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-theme-primary-700 hover:bg-theme-primary-100 transition-colors">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4"/>
          </svg>
          Kalender
        </button>
      </div>
    </ng-container>

    <ng-template #respond>
      <!-- ===== respondable variant: modern accept / decline + optional comment ===== -->
      <div *ngIf="canRespond()" class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <button type="button" (click)="eventService.setResponse(event, true, event.employeeEventResponse.comments)"
                  class="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors"
                  [ngClass]="event.employeeEventResponse.active === true
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'bg-green-50 text-green-700 hover:bg-green-100'">
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                 stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            Annehmen
          </button>
          <button type="button" (click)="eventService.setResponse(event, false, event.employeeEventResponse.comments)"
                  class="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors"
                  [ngClass]="event.employeeEventResponse.active === false
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'bg-red-50 text-red-700 hover:bg-red-100'">
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                 stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
            Ablehnen
          </button>
          <button type="button" (click)="isExpanded = !isExpanded"
                  title="Anmerkung {{ event.employeeEventResponse.comments != '' ? 'bearbeiten' : 'hinzufügen' }}"
                  class="flex-none inline-flex items-center justify-center h-10 w-10 rounded-xl transition-colors"
                  [ngClass]="event.employeeEventResponse.comments != '' || isExpanded
                    ? 'bg-theme-primary-50 text-theme-primary-700'
                    : 'bg-gray-50 text-gray-400 hover:text-gray-600'">
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
        </div>

        <textarea *ngIf="isExpanded" #comment
                  [ngModel]="event.employeeEventResponse.comments"
                  (focusout)="eventService.setResponse(event, event.employeeEventResponse.active ?? true, comment.value)"
                  placeholder="Anmerkung…" rows="2"
                  class="w-full rounded-xl border border-gray-200 bg-white p-2.5 text-sm shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 placeholder-gray-300"></textarea>
      </div>

      <!-- response window closed, but a decision exists -->
      <p *ngIf="!canRespond() && event.employeeEventResponse.active !== undefined"
         class="text-xs text-gray-400">
        Beantwortet am {{ event.employeeEventResponse.modified | date: "dd.MM.yyyy" }}
      </p>
    </ng-template>
  </div>
</div>
